import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Copy, Check, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const InviteMembers = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchGroup();
  }, [groupId]);

  const fetchGroup = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}`);
      setGroup(response.data);
    } catch (error) {
      toast.error('Failed to fetch group details');
    } finally {
      setLoading(false);
    }
  };

  const inviteLink = group ? `${window.location.origin}/join/${groupId}?code=${group.invite_code}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${group.name} on SharePay`,
          text: `Join my savings group "${group.name}" and let's save together!`,
          url: inviteLink
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <div className="px-4 md:px-8 py-6 border-b border-border">
        <Button
          variant="ghost"
          onClick={() => navigate(`/group/${groupId}`)}
          className="-ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Group
        </Button>
      </div>

      <div className="px-4 md:px-8 py-8 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-card border border-border p-8 shadow-lg text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center mx-auto mb-4">
            <Share2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-manrope mb-2" data-testid="invite-title">Invite Members</h1>
          <p className="text-sm text-muted-foreground mb-8">Share this link or QR code to invite friends to {group.name}</p>

          <div className="bg-accent/50 border border-border rounded-2xl p-6 mb-6">
            <div className="bg-white p-4 rounded-xl mx-auto w-fit mb-4">
              <QRCodeSVG value={inviteLink} size={200} data-testid="qr-code" />
            </div>
            <p className="text-xs text-muted-foreground">Scan QR code to join</p>
          </div>

          <div className="mb-6">
            <div className="flex gap-2">
              <Input
                value={inviteLink}
                readOnly
                className="rounded-xl font-mono text-sm"
                data-testid="invite-link-input"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="rounded-xl shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleShare}
              className="w-full rounded-full py-6 font-semibold"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5 mr-2" /> Share Invite Link
            </Button>
            <p className="text-xs text-muted-foreground">
              Invite Code: <span className="font-mono font-semibold" data-testid="invite-code">{group.invite_code}</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InviteMembers;